<template>
  <div class="region-manage-page">
    <!-- 头部 -->
    <header class="page-header">
      <h1>{{ $t('admin.RegionManage.line5_text') }}</h1>
      <button class="add-btn" @click="openAddDialog">{{ $t('admin.RegionManage.line6_text') }}</button>
    </header>

    <!-- 搜索栏 -->
    <div class="search-bar">
      <input
        v-model="searchQuery"
        type="text"
        :placeholder="$t('admin.RegionManage.line14_placeholder')"
        class="search-input"
        @input="handleSearch"
      />
      <label class="filter-label">
        <input type="checkbox" v-model="showOnlyEnabled" @change="handleSearch" />
        只显示启用的
      </label>
    </div>

    <!-- 区域列表 -->
    <div class="region-list">
      <div v-for="region in filteredRegions" :key="region.id" class="region-item">
        <div class="region-info">
          <div class="region-header">
            <span class="region-code">{{ region.regionCode }}</span>
            <span class="region-name">{{ region.regionName }}</span>
            <span :class="['status-tag', region.enabled ? 'enabled' : 'disabled']">
              {{ region.enabled ? '启用' : '禁用' }}
            </span>
          </div>
          <div class="region-desc">{{ region.description || '无描述' }}</div>
          <div class="region-meta">
            <span>排序：{{ region.sortOrder }}</span>
            <span>创建：{{ formatDate(region.createdAt) }}</span>
          </div>
        </div>
        <div class="region-actions">
          <button class="action-btn edit" @click="openEditDialog(region)">{{ $t('common.edit') }}</button>
          <button class="action-btn delete" @click="deleteRegion(region.id)">{{ $t('common.delete') }}</button>
        </div>
      </div>

      <div v-if="filteredRegions.length === 0" class="empty-state">
        暂无区域数据
      </div>
    </div>

    <!-- 新增/编辑弹窗 -->
    <div v-if="dialogVisible" 
         style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 999999;"
         @click="closeDialog">
      <div style="background: white; border-radius: 12px; width: 90%; max-width: 500px; max-height: 90vh; overflow-y: auto; box-shadow: 0 10px 40px rgba(0,0,0,0.3);"
           @click.stop>
        <div style="display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #eee;">
          <h2 style="font-size: 18px; color: #333; margin: 0;">{{ isEdit ? '编辑区域' : '新增区域' }}</h2>
          <button style="background: none; border: none; font-size: 24px; color: #999; cursor: pointer;" @click="closeDialog">×</button>
        </div>
        <div style="padding: 20px;">
          <div class="form-group">
            <label>{{ $t('admin.RegionManage.line64_text') }} <span class="required">*</span></label>
            <input
              v-model="formData.regionCode"
              type="text"
              :placeholder="$t('admin.RegionManage.line68_placeholder')"
              class="form-input"
              :disabled="isEdit"
            />
            <div class="form-tip">{{ $t('admin.RegionManage.line72_text') }}</div>
          </div>

          <div class="form-group">
            <label>{{ $t('admin.RegionManage.line76_text') }} <span class="required">*</span></label>
            <input
              v-model="formData.regionName"
              type="text"
              :placeholder="$t('admin.RegionManage.line80_placeholder')"
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label>{{ $t('admin.RegionManage.line86_text') }}</label>
            <textarea
              v-model="formData.description"
              :placeholder="$t('admin.RegionManage.line89_placeholder')"
              class="form-textarea"
              rows="3"
            ></textarea>
          </div>

          <div class="form-group">
            <label>{{ $t('admin.RegionManage.line96_text') }}</label>
            <input
              v-model.number="formData.sortOrder"
              type="number"
              :placeholder="$t('admin.RegionManage.line100_placeholder')"
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" v-model="formData.enabled" />
              启用该区域
            </label>
          </div>
        </div>
        <div style="display: flex; gap: 12px; padding: 16px 20px; border-top: 1px solid #eee;">
          <button style="flex: 1; padding: 12px; border: none; border-radius: 6px; font-size: 14px; font-weight: 600; cursor: pointer; background: #f0f0f0; color: #666;" @click="closeDialog">{{ $t('common.cancel') }}</button>
          <button style="flex: 1; padding: 12px; border: none; border-radius: 6px; font-size: 14px; font-weight: 600; cursor: pointer; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white;" @click="submitForm" :disabled="submitting">
            {{ submitting ? '提交中...' : '确定' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'RegionManage',
  data() {
    return {
      regions: [],
      searchQuery: '',
      showOnlyEnabled: false,
      dialogVisible: false,
      isEdit: false,
      submitting: false,
      formData: {
        id: null,
        regionCode: '',
        regionName: '',
        description: '',
        enabled: true,
        sortOrder: 0
      }
    };
  },
  computed: {
    filteredRegions() {
      let regions = this.regions;

      // 启用筛选
      if (this.showOnlyEnabled) {
        regions = regions.filter(r => r.enabled);
      }

      return regions;
    }
  },
  created() {
    this.loadRegions();
  },
  methods: {
    // 加载区域列表
    async loadRegions() {
      try {
        const params = {};
        if (this.searchQuery && this.searchQuery.trim()) {
          params.keyword = this.searchQuery.trim();
        }
        if (this.showOnlyEnabled) {
          params.enabled = true;
        }
        const res = await api.get('/admin/regions', { params });
        this.regions = res.data?.data || res || [];
      } catch (error) {
        console.error('加载区域失败:', error);
        this.$toast?.error('加载区域失败');
      }
    },

    // 搜索处理
    handleSearch() {
      this.loadRegions();
    },

    // 打开新增弹窗
    openAddDialog() {
      this.isEdit = false;
      this.formData = {
        id: null,
        regionCode: '',
        regionName: '',
        description: '',
        enabled: true,
        sortOrder: 0
      };
      this.dialogVisible = true;
    },

    // 打开编辑弹窗
    openEditDialog(region) {
      this.isEdit = true;
      this.formData = {
        id: region.id,
        regionCode: region.regionCode,
        regionName: region.regionName,
        description: region.description || '',
        enabled: region.enabled,
        sortOrder: region.sortOrder || 0
      };
      this.dialogVisible = true;
    },

    // 关闭弹窗
    closeDialog() {
      this.dialogVisible = false;
    },

    // 提交表单
    async submitForm() {
      // 验证
      if (!this.formData.regionCode || !this.formData.regionName) {
        this.$toast?.error('请填写区域编号和区域名称');
        return;
      }

      this.submitting = true;

      try {
        if (this.isEdit) {
          // 更新
          await api.put(`/admin/regions/${this.formData.id}`, this.formData);
          this.$toast?.success('更新成功');
        } else {
          // 新增
          await api.post('/admin/regions', this.formData);
          this.$toast?.success('新增成功');
        }

        this.closeDialog();
        this.loadRegions();
      } catch (error) {
        console.error('提交失败:', error);
        if (error.response?.status === 400) {
          this.$toast?.error('区域编号已存在');
        } else {
          this.$toast?.error('操作失败');
        }
      } finally {
        this.submitting = false;
      }
    },

    // 删除区域
    async deleteRegion(id) {
      if (!confirm('确定要删除该区域吗？')) return;

      try {
        await api.delete(`/admin/regions/${id}`);
        this.$toast?.success('删除成功');
        this.loadRegions();
      } catch (error) {
        console.error('删除失败:', error);
        this.$toast?.error('删除失败');
      }
    },

    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      return date.toLocaleDateString('zh-CN');
    }
  }
};
</script>

<style scoped>
.region-manage-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h1 {
  font-size: 20px;
  color: #333;
  margin: 0;
}

.add-btn {
  padding: 10px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.add-btn:hover {
  opacity: 0.9;
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  background: white;
  padding: 16px;
  border-radius: 8px;
}

.search-input {
  flex: 1;
  padding: 10px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
}

.search-input:focus {
  outline: none;
  border-color: #667eea;
}

.filter-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 14px;
  cursor: pointer;
}

.region-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.region-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.region-info {
  flex: 1;
}

.region-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.region-code {
  font-size: 14px;
  color: #667eea;
  font-weight: 600;
  background: #f0f4ff;
  padding: 4px 8px;
  border-radius: 4px;
}

.region-name {
  font-size: 16px;
  font-weight: bold;
  color: #333;
}

.status-tag {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
}

.status-tag.enabled {
  background: #f6ffed;
  color: #52c41a;
}

.status-tag.disabled {
  background: #f5f5f5;
  color: #999;
}

.region-desc {
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
}

.region-meta {
  font-size: 12px;
  color: #999;
  display: flex;
  gap: 16px;
}

.region-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  padding: 6px 16px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
}

.action-btn.edit {
  background: #f0f0f0;
  color: #666;
}

.action-btn.edit:hover {
  background: #e0e0e0;
}

.action-btn.delete {
  background: #ff4d4f;
  color: white;
}

.action-btn.delete:hover {
  background: #ff7875;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #999;
  font-size: 14px;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  background: rgba(0,0,0,0.5) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 999999 !important;
}

.modal {
  background: white !important;
  border-radius: 12px !important;
  width: 90% !important;
  max-width: 500px !important;
  max-height: 90vh !important;
  overflow-y: auto !important;
  box-shadow: 0 10px 40px rgba(0,0,0,0.3) !important;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h2 {
  font-size: 18px;
  color: #333;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.required {
  color: #ff4d4f;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #667eea;
}

.form-tip {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: normal;
}

.modal-footer {
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #eee;
}

.modal-btn {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.modal-btn.cancel {
  background: #f0f0f0;
  color: #666;
}

.modal-btn.confirm {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.modal-btn.confirm:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>

<style>
/* 弹窗样式 - 不使用 scoped，确保全局生效 */
.modal-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  background: rgba(0,0,0,0.5) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 999999 !important;
}

.modal {
  background: white !important;
  border-radius: 12px !important;
  width: 90% !important;
  max-width: 500px !important;
  max-height: 90vh !important;
  overflow-y: auto !important;
  box-shadow: 0 10px 40px rgba(0,0,0,0.3) !important;
  animation: modalSlideIn 0.3s ease-out !important;
}

@keyframes modalSlideIn {
  from {
    transform: translateY(-50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
